import { Badge, Spinner } from '~/elements'
import { ReactNode, useState } from 'react'
import { cn, dollars } from '~/utils'
import { ClientOutputs } from '@botpress/client'
import { billingPlan } from '~/features/billing/constants'
import {
  type BillingVersion,
  COMMUNITY_PLAN_PRODUCT_ID,
  PLUS_PLAN_PRODUCT_ID,
  TEAM_PLAN_PRODUCT_ID,
  useBilling,
} from '~/features/usage/useBilling'
import { getQueryOptions } from '~/services'
import { useMonthlyCheckout, useYearlyCheckout, useCustomerPortal } from '~/hooks'
import { computeTotalCost } from '~/features/billing/utils'
import { useSuspenseQuery } from '@tanstack/react-query'
import { isSubscribedToPrice, getPlanPricing, isDowngradeFromYearly } from '~/features/billing/helpers'
import { PlanInclusions } from '~/features/billing/PlanInclusions'
import {
  Button,
  Card,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Label,
  Skeleton,
  Switch,
  showConfirmationPrompt,
} from '@bpinternal/ui-kit-next'
import { useRouter } from '@tanstack/react-router'

const showYearlyDowngradeDialog = (openCustomerPortal: () => void) => {
  void showConfirmationPrompt(
    <p>
      You must first cancel your current plan and resubscribe at the end of your billing cycle.
      <div className="py-2">
        <Button onClick={() => openCustomerPortal()} className="hover:cursor-pointer hover:underline">
          Manage your subscription
        </Button>
      </div>
    </p>,
    {
      confirmLabel: 'OK',
      title: 'Subscription management',
    }
  )
}

type PlanSelectionProps = { workspaceId: string; isDialog: boolean }

export const PlanSelection = (props: PlanSelectionProps) => {
  const { workspaceId, isDialog } = props
  const navigate = useRouter().navigate
  const { isLoading, ...billing } = useBilling(workspaceId)

  //Avoid using cache since we can't invalidate it when customer changes plan in stripe customer portal
  const { plan } = useSuspenseQuery({
    ...getQueryOptions('workspaces_/$workspaceId_', { workspaceId }),
    queryKey: [],
    gcTime: 0,
    staleTime: 0,
  }).data

  const { setProductQuantity, productQuantityMap, promoCode, subscriptions } = billing
  const [isYearly, setIsYearly] = useState(false)

  const onUpdateSubscription = () => {
    void navigate({
      to: '/workspaces/$workspaceId/billing',
      params: { workspaceId },
    })
  }

  const { mutate: handleMonthlyCheckout } = useMonthlyCheckout(workspaceId, billing, onUpdateSubscription)

  const { mutate: handleYearlyCheckout } = useYearlyCheckout(workspaceId, billing, onUpdateSubscription)

  const { mutate: openCustomerPortal } = useCustomerPortal(workspaceId)

  const {
    price: teamPrice,
    pricePerMonth: teamPricePerMonth,
    pricePerYear: teamPricePerYear,
  } = getPlanPricing(TEAM_PLAN_PRODUCT_ID, isYearly, productQuantityMap)
  const {
    price: plusPrice,
    pricePerMonth: plusPricePerMonth,
    pricePerYear: plusPricePerYear,
  } = getPlanPricing(PLUS_PLAN_PRODUCT_ID, isYearly, productQuantityMap)

  const communityPrice = billing.products?.find((product) => product.id === COMMUNITY_PLAN_PRODUCT_ID)?.prices[0]

  const isLegacyPricing = billing.billingVersion === 'v1' || billing.billingVersion === 'v2'

  let discountedTeamPlanPrice: number | undefined = undefined
  if (promoCode?.coupon.appliesTo?.includes(TEAM_PLAN_PRODUCT_ID)) {
    const monthlyEquivalent = (teamPrice?.unit_amount ?? 0) / 100 / (isYearly ? 12 : 1)
    discountedTeamPlanPrice = monthlyEquivalent * (1 - promoCode.coupon.percentOff / 100)
  }

  let discountedPlusPlanPrice: number | undefined = undefined
  if (promoCode?.coupon.appliesTo?.includes(PLUS_PLAN_PRODUCT_ID)) {
    const monthlyEquivalent = (plusPrice?.unit_amount ?? 0) / 100 / (isYearly ? 12 : 1)
    discountedPlusPlanPrice = monthlyEquivalent * (1 - promoCode.coupon.percentOff / 100)
  }

  const billingReadonly = billing.billingInfo?.billingReadonly
  const isEnterprise = plan === 'enterprise' || billingReadonly

  const isPlusButtonDisabled = isEnterprise || isSubscribedToPrice(plusPrice?.id ?? '', subscriptions ?? [])
  const isTeamButtonDisabled = isEnterprise || isSubscribedToPrice(teamPrice?.id ?? '', subscriptions ?? [])

  const planProductIds = [COMMUNITY_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID]
  const currentPlanPrice = (subscriptions ?? [])
    .flatMap((sub) => sub.items.data)
    .find((item) => planProductIds.includes(item.price.product.toString()))?.price

  const CommunityPlanCard = () => (
    <PlanCard
      plan="community"
      billingVersion={billing.billingVersion}
      price="$0"
      action={
        <Button
          disabled={plan === 'community' || plan === 'enterprise' || billingReadonly}
          variant="outline"
          onClick={() => {
            if (currentPlanPrice && communityPrice && isDowngradeFromYearly(currentPlanPrice, communityPrice)) {
              showYearlyDowngradeDialog(openCustomerPortal)
              return
            }
            setProductQuantity(COMMUNITY_PLAN_PRODUCT_ID, 1)
            setProductQuantity(TEAM_PLAN_PRODUCT_ID, 0)
            setProductQuantity(PLUS_PLAN_PRODUCT_ID, 0)
            void showConfirmationPrompt(
              <p>
                After updating to the Pay-as-you-go plan, your new monthly bill will be
                <span className="font-bold"> ${computeTotalCost(productQuantityMap, promoCode)}</span> per month. Are
                you sure you want to proceed?
              </p>,
              {
                confirmLabel: 'Update plan',
                title: 'Do you want to proceed?',
              }
            ).then(() => {
              handleMonthlyCheckout()
            })
          }}
        >
          {plan === 'community' ? 'Current plan' : 'Update plan'}
        </Button>
      }
    />
  )

  const PlusPlanCard = () => (
    <PlanCard
      color={plan !== 'enterprise' ? 'blue' : undefined}
      plan="plus"
      billingVersion={billing.billingVersion}
      price={
        <div className="flex flex-col pt-5">
          <p>${discountedPlusPlanPrice !== undefined ? dollars(discountedPlusPlanPrice) : plusPricePerMonth}</p>
          {discountedPlusPlanPrice !== undefined && (
            <p className="text-sm text-muted-foreground line-through">${plusPricePerMonth}</p>
          )}
        </div>
      }
      action={
        <Button
          className="disabled:bg-gray-5 disabled:text-gray-11"
          disabled={isPlusButtonDisabled}
          onClick={() => {
            if (currentPlanPrice && plusPrice && isDowngradeFromYearly(currentPlanPrice, plusPrice)) {
              showYearlyDowngradeDialog(openCustomerPortal)
              return
            }

            if (isYearly) {
              handleYearlyCheckout({
                ...productQuantityMap[PLUS_PLAN_PRODUCT_ID]!,
                priceId: plusPrice?.id ?? '',
                quantity: 1,
              })
            }

            if (!isYearly) {
              setProductQuantity(PLUS_PLAN_PRODUCT_ID, 1)
              setProductQuantity(TEAM_PLAN_PRODUCT_ID, 0)
              setProductQuantity(COMMUNITY_PLAN_PRODUCT_ID, 0)
              void showConfirmationPrompt(
                <p>
                  After switching to the Plus plan, your new monthly bill will be
                  <span className="font-bold"> ${dollars(computeTotalCost(productQuantityMap, promoCode))}</span> per
                  month. Are you sure you want to proceed?
                </p>,
                {
                  confirmLabel: 'Switch to Plus plan',
                  title: 'Do you want to proceed?',
                }
              ).then(() => {
                handleMonthlyCheckout()
              })
            }
          }}
        >
          {isPlusButtonDisabled ? 'Current plan' : 'Buy plan'}
        </Button>
      }
    />
  )

  const TeamPlanCard = () => (
    <PlanCard
      color={plan !== 'enterprise' ? 'grass' : undefined}
      plan="team"
      billingVersion={billing.billingVersion}
      badge={plan !== 'enterprise' && isYearly ? 'Best value' : undefined}
      price={
        <div className="flex flex-col">
          <p>${discountedTeamPlanPrice !== undefined ? dollars(discountedTeamPlanPrice) : teamPricePerMonth}</p>
          {discountedTeamPlanPrice !== undefined && (
            <p className="text-sm text-muted-foreground line-through">${teamPricePerMonth}</p>
          )}
        </div>
      }
      action={
        <Button
          className="hover:bg-grass-10/90 bg-grass-10 disabled:bg-gray-5 disabled:text-gray-11"
          disabled={isTeamButtonDisabled}
          onClick={() => {
            if (currentPlanPrice && teamPrice && isDowngradeFromYearly(currentPlanPrice, teamPrice)) {
              showYearlyDowngradeDialog(openCustomerPortal)
              return
            }

            if (isYearly) {
              handleYearlyCheckout({
                ...productQuantityMap[TEAM_PLAN_PRODUCT_ID]!,
                priceId: teamPrice?.id ?? '',
                quantity: 1,
              })
            }

            if (!isYearly) {
              setProductQuantity(TEAM_PLAN_PRODUCT_ID, 1)
              setProductQuantity(COMMUNITY_PLAN_PRODUCT_ID, 0)
              setProductQuantity(PLUS_PLAN_PRODUCT_ID, 0)
              void showConfirmationPrompt(
                <p>
                  After upgrading to the Team plan, your new monthly bill will be
                  <span className="font-bold"> ${dollars(computeTotalCost(productQuantityMap, promoCode))}</span> per
                  month. Are you sure you want to proceed?
                </p>,
                {
                  confirmLabel: 'Upgrade to Team plan',
                  title: 'Do you want to proceed?',
                }
              ).then(() => {
                handleMonthlyCheckout()
              })
            }
          }}
        >
          {isTeamButtonDisabled ? 'Current plan' : 'Buy plan'}
        </Button>
      }
    />
  )

  const EnterprisePlanCard = () => (
    <PlanCard
      plan="enterprise"
      billingVersion={billing.billingVersion}
      color={plan === 'enterprise' ? 'purple' : undefined}
      badge={plan === 'enterprise' ? 'Current plan' : undefined}
      price={
        <div className="flex flex-col items-baseline gap-1">
          <p>$2000</p>
        </div>
      }
      action={
        <a href="https://botpress.com/contact-us" target="_blank" rel="noreferrer">
          <Button className="w-full" variant="outline">
            Contact us
          </Button>
        </a>
      }
    />
  )

  const skeletonCard = () => (
    <Card className="max-h-[calc(100vh-184px)] w-full p-7 text-start">
      <div className="grid h-full grid-rows-[auto_minmax(96px,auto)_auto_auto_1fr] gap-2 lg:gap-4">
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    </Card>
  )

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-6 @container">
        <div
          className={cn(
            'grid h-full grid-cols-1 gap-6',
            isLegacyPricing ? '@3xl:grid-cols-3' : '@2xl:grid-cols-2  @4xl:grid-cols-4'
          )}
        >
          {Array.from({ length: isLegacyPricing ? 3 : 4 }).map(() => skeletonCard())}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-6 @container">
      <div className="flex items-center justify-center gap-2">
        <Switch id="billing-yearly" checked={isYearly} onCheckedChange={setIsYearly} />
        <Label htmlFor="billing-yearly">Billed annually</Label>
        <Badge variant="outline" color="purple">
          Save 10%
        </Badge>
      </div>
      <div
        className={cn(
          'grid h-full grid-cols-1 gap-6',
          isLegacyPricing ? '@3xl:grid-cols-3' : '@2xl:grid-cols-2  @4xl:grid-cols-4',
          isDialog && 'hidden @3xl:grid @3xl:grid-cols-3 @5xl:grid-cols-3'
        )}
      >
        {!isDialog && <CommunityPlanCard />}
        {!isLegacyPricing && <PlusPlanCard />}
        <TeamPlanCard />
        <EnterprisePlanCard />
      </div>
      <div className={cn(' h-min w-full items-center justify-center', isDialog ? 'flex @3xl:hidden' : 'hidden')}>
        <Carousel className="flex w-[calc(100%-64px)] items-center @xl:w-1/2">
          <CarouselContent>
            {!isLegacyPricing && (
              <CarouselItem className="flex items-center justify-center ">
                <PlusPlanCard />
              </CarouselItem>
            )}
            <CarouselItem className="flex items-center justify-center ">
              <TeamPlanCard />
            </CarouselItem>
            <CarouselItem className="flex items-center justify-center ">
              <EnterprisePlanCard />
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
    </div>
  )
}

type PlanCardProps = {
  color?: 'grass' | 'blue' | 'purple'
  plan: ClientOutputs['getWorkspace']['plan']
  badge?: ReactNode
  price?: ReactNode
  action: ReactNode
  billingVersion: BillingVersion
}

const PlanCard = ({ color, action, price, plan, badge, billingVersion }: PlanCardProps) => {
  return (
    <Card
      className={cn(
        ' max-h-[calc(100vh-184px)] w-full p-7 text-start',
        { 'border-2': color },
        {
          'border-accent-6': color === 'blue',
          'border-grass-6': color === 'grass',
          'border-purple-10': color === 'purple',
        }
      )}
    >
      <div className="grid h-full grid-rows-[auto_minmax(96px,auto)_auto_auto_1fr] gap-2 lg:gap-4">
        <div className="flex items-start justify-between">
          <p
            className={cn(
              { 'text-accent-11': color === 'blue' },
              { 'text-grass-11': color === 'grass' },
              { 'text-purple-11': color === 'purple' },
              'text-lg font-medium'
            )}
          >
            {billingPlan[plan].name}
          </p>
          {badge && (
            <Badge className="border-none" variant="secondary" color={color}>
              {badge}
            </Badge>
          )}
        </div>
        <div className="my-2 flex min-h-20 flex-col justify-center lg:my-4">
          <p className={cn('text-sm text-muted-foreground', plan !== 'enterprise' && 'hidden')}>Starting at:</p>
          <p className=" flex flex-wrap items-baseline gap-2 text-2xl font-bold">
            {price}
            <span className="text-sm text-muted-foreground">/mo</span>
            {plan !== 'enterprise' && <span className="text-nowrap text-2xl font-bold">+ AI Spend</span>}
          </p>
          <p className={cn('text-sm text-muted-foreground', plan !== 'enterprise' && 'hidden')}>on 3-year term</p>
        </div>
        {action}
        <p className="text-sm">{billingPlan[plan].description}</p>
        <PlanInclusions plan={plan} billingVersion={billingVersion} />
      </div>
    </Card>
  )
}
